// =============================================================================
// src/services/unleashedLookupSync.ts
// -----------------------------------------------------------------------------
// PURE compare/plan for the lookup_options-backed reference syncs (Warehouses,
// Customer Types, Taxes). Given the slim rows fetched from Unleashed and our
// current lookup_options rows for a category, it produces the plan the Sync
// Utilities screen renders. Writes nothing — the UI applies via the lookups repo.
//
// matchBy: "external_ref" for warehouses/taxes (matched on the code) or "value"
// for customer types (matched on the name, since the hand-seed has no external_ref).
// =============================================================================

import type { SlimLookup } from "../../supabase/functions/_shared/unleashed/lookupMap";
import type { LookupOption } from "../data/lookups";

export interface LookupChange {
  field: "name" | "status";
  from: string | null;
  to: string | null;
}

export interface LookupUpdate {
  id: string;
  key: string;
  value: string;
  externalRef: string | null;
  changes: LookupChange[];
}

export interface LookupObsolete {
  id: string;
  key: string;
  value: string;
}

export interface LookupSyncPlan {
  toCreate: SlimLookup[];
  toUpdate: LookupUpdate[];
  toObsolete: LookupObsolete[];
  unchangedCount: number;
  skippedNewObsolete: number;
}

export function planLookupSync(
  incoming: SlimLookup[],
  current: LookupOption[],
  matchBy: "external_ref" | "value"
): LookupSyncPlan {
  const map = new Map<string, LookupOption>();
  for (const r of current) {
    const k = matchBy === "external_ref" ? r.external_ref : r.value;
    if (k) map.set(k, r);
  }

  const plan: LookupSyncPlan = {
    toCreate: [],
    toUpdate: [],
    toObsolete: [],
    unchangedCount: 0,
    skippedNewObsolete: 0,
  };

  for (const inc of incoming) {
    if (!inc.key) continue;
    const cur = map.get(inc.key);

    if (!cur) {
      if (inc.obsolete) plan.skippedNewObsolete += 1;
      else plan.toCreate.push(inc);
      continue;
    }

    if (inc.obsolete && !cur.deactivated) {
      plan.toObsolete.push({ id: cur.id, key: inc.key, value: cur.value });
      continue;
    }

    const changes: LookupChange[] = [];
    if ((cur.value ?? null) !== (inc.value ?? null)) {
      changes.push({ field: "name", from: cur.value ?? null, to: inc.value ?? null });
    }
    if (cur.deactivated && !inc.obsolete) {
      changes.push({ field: "status", from: "deactivated", to: "active" });
    }

    if (changes.length > 0) {
      plan.toUpdate.push({
        id: cur.id,
        key: inc.key,
        value: inc.value,
        externalRef: inc.externalRef,
        changes,
      });
    } else {
      plan.unchangedCount += 1;
    }
  }

  return plan;
}
